import { useEffect, useState } from 'react';
import { Check, ChevronsUpDown, Hash, Lock } from 'lucide-react';
import { api } from '../contexts/AuthContext';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from './ui/command';

// Module-level cache so we don't refetch on every dialog open.
let channelsCache = null;
let channelsErrorCache = null;

export const invalidateSlackChannelsCache = () => {
  channelsCache = null;
  channelsErrorCache = null;
};

export default function SlackChannelPicker({
  value,
  onChange,
  placeholder = 'Select a Slack channel...',
  testId = 'slack-channel-picker',
  allowClear = true,
}) {
  const [open, setOpen] = useState(false);
  const [channels, setChannels] = useState(channelsCache || []);
  const [loading, setLoading] = useState(!channelsCache);
  const [error, setError] = useState(channelsErrorCache);

  useEffect(() => {
    if (channelsCache) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get('/slack/channels');
        if (cancelled) return;
        channelsCache = res.data.channels || [];
        channelsErrorCache = res.data.error || null;
        setChannels(channelsCache);
        setError(channelsErrorCache);
      } catch (err) {
        if (cancelled) return;
        channelsErrorCache = err.response?.data?.detail || 'Failed to load channels';
        setError(channelsErrorCache);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Fallback to raw input if bot token is missing or list fetch failed.
  if (!loading && (error || channels.length === 0)) {
    return (
      <div>
        <Input
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          placeholder="C0123456789 (channel ID)"
          className="rounded-sm border-[#E2E8F0]"
          data-testid={`${testId}-fallback-input`}
        />
        {error && (
          <p className="text-[10px] text-[#e74c3c] mt-1">
            Channel list unavailable: {error}. Paste the channel ID manually.
          </p>
        )}
      </div>
    );
  }

  const selected = channels.find((c) => c.id === value);
  const label = selected
    ? `${selected.is_private ? '🔒 ' : '# '}${selected.name}`
    : (loading ? 'Loading channels...' : placeholder);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          className="w-full justify-between rounded-sm border-[#E2E8F0] font-normal"
          data-testid={testId}
          disabled={loading}
        >
          <span className="truncate">{label}</span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[320px] p-0" align="start">
        <Command>
          <CommandInput placeholder="Search channels..." data-testid={`${testId}-search`} />
          <CommandList>
            <CommandEmpty>No channels found.</CommandEmpty>
            <CommandGroup>
              {allowClear && (
                <CommandItem
                  value="__none__"
                  onSelect={() => { onChange(''); setOpen(false); }}
                  data-testid={`${testId}-none`}
                >
                  <Check className={`mr-2 h-4 w-4 ${!value ? 'opacity-100' : 'opacity-0'}`} />
                  <span className="text-[#7F8C8D]">None (use global / skip)</span>
                </CommandItem>
              )}
              {channels.map((c) => (
                <CommandItem
                  key={c.id}
                  value={`${c.name} ${c.id}`}
                  onSelect={() => { onChange(c.id); setOpen(false); }}
                  data-testid={`${testId}-option-${c.id}`}
                >
                  <Check className={`mr-2 h-4 w-4 ${value === c.id ? 'opacity-100' : 'opacity-0'}`} />
                  {c.is_private ? <Lock size={12} className="mr-1.5 text-[#7F8C8D]" /> : <Hash size={12} className="mr-1.5 text-[#7F8C8D]" />}
                  <span className="truncate">{c.name}</span>
                  {!c.is_member && <span className="ml-auto text-[10px] text-[#BDC3C7]">not joined</span>}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
